import { useState, useEffect, useCallback } from "react"
import { X, Loader2, Save, AlertCircle, Check, DollarSign } from "lucide-react"
import { InsuranceCarrierService } from "../../services/insurance-carrier.service"
import type {
  CarrierStateMatrix,
  CarrierStateMatrixCell,
  CampaignCarrierConfigInput
} from "../../types"

interface InsuranceCarriersModalProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  campaignName?: string
}

export default function InsuranceCarriersModal({
  isOpen,
  onClose,
  campaignId,
  campaignName
}: InsuranceCarriersModalProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [matrix, setMatrix] = useState<CarrierStateMatrix | null>(null)
  const [pendingChanges, setPendingChanges] = useState<Map<string, CampaignCarrierConfigInput>>(new Map())
  const [editingCell, setEditingCell] = useState<string | null>(null)
  const [tempCommissionRate, setTempCommissionRate] = useState<string>("")

  const loadMatrix = useCallback(async () => {
    if (!campaignId) return

    try {
      setLoading(true)
      setError(null)
      const response = await InsuranceCarrierService.getCarrierStateMatrix(campaignId)
      if (response.data) {
        setMatrix(response.data)
      } else {
        setError(response.error || "Failed to load carrier configurations")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load carrier configurations")
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => {
    if (isOpen && campaignId) {
      loadMatrix()
      setPendingChanges(new Map())
    }
  }, [isOpen, campaignId, loadMatrix])

  const getCellKey = (stateCode: string, carrierId: string) => `${stateCode}_${carrierId}`

  const getCellValue = (stateCode: string, carrierId: string): CarrierStateMatrixCell | null => {
    if (!matrix) return null

    // Check pending changes first
    const key = getCellKey(stateCode, carrierId)
    const pending = pendingChanges.get(key)

    if (pending) {
      const carrier = matrix.carriers.find(c => c.id === carrierId)
      return {
        carrierId,
        carrierCode: carrier?.code || "",
        carrierName: carrier?.name || "",
        stateCode,
        isAvailable: pending.isAvailable,
        commissionRate: pending.commissionRate ?? null
      }
    }

    return matrix.configs[stateCode]?.[carrierId] || null
  }

  const handleCellClick = (stateCode: string, carrierId: string) => {
    const key = getCellKey(stateCode, carrierId)
    setEditingCell(key)
    const cell = getCellValue(stateCode, carrierId)
    setTempCommissionRate(cell?.commissionRate?.toString() || "")
  }

  const handleSetUnavailable = (stateCode: string, carrierId: string) => {
    const key = getCellKey(stateCode, carrierId)
    setPendingChanges(prev => {
      const updated = new Map(prev)
      updated.set(key, {
        carrierId,
        stateCode,
        isAvailable: false,
        commissionRate: null
      })
      return updated
    })
    setEditingCell(null)
  }

  const handleSaveCommission = (stateCode: string, carrierId: string) => {
    const rate = parseFloat(tempCommissionRate)
    const key = getCellKey(stateCode, carrierId)

    if (isNaN(rate) || rate < 0) {
      return
    }

    setPendingChanges(prev => {
      const updated = new Map(prev)
      updated.set(key, {
        carrierId,
        stateCode,
        isAvailable: true,
        commissionRate: rate
      })
      return updated
    })
    setEditingCell(null)
    setTempCommissionRate("")
  }

  const handleSaveAll = async () => {
    if (pendingChanges.size === 0) {
      onClose()
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      const configs = Array.from(pendingChanges.values())
      const response = await InsuranceCarrierService.bulkUpsertCarrierConfigs(campaignId, configs)

      if (response.data) {
        setSuccessMessage(`Saved ${response.data.created + response.data.updated} configurations`)
        setPendingChanges(new Map())
        await loadMatrix()

        // Auto-close after success
        setTimeout(() => {
          onClose()
        }, 1500)
      } else {
        setError(response.error || "Failed to save configurations")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save configurations")
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (pendingChanges.size > 0) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        return
      }
    }
    setEditingCell(null)
    setPendingChanges(new Map())
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Insurance Carriers Configuration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure carrier availability and commission rates for {campaignName || "this campaign"}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white/50 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-cyan-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading carrier configurations...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 font-medium">{error}</p>
                <button
                  onClick={loadMatrix}
                  className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : matrix && matrix.states.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-700 font-medium">No states configured for this campaign</p>
                <p className="text-gray-500 text-sm mt-2">
                  Please configure allowed states in the campaign settings first.
                </p>
              </div>
            </div>
          ) : matrix ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="sticky left-0 z-10 bg-gray-50 p-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 min-w-[100px]">
                      State
                    </th>
                    {matrix.carriers.map(carrier => (
                      <th
                        key={carrier.id}
                        className="p-3 text-center font-semibold text-gray-700 border-b-2 border-gray-200 min-w-[120px]"
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-sm">{carrier.shortName || carrier.code}</span>
                          <span className="text-xs text-gray-500 font-normal">{carrier.name}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.states.map((state, stateIndex) => (
                    <tr key={state} className={stateIndex % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                      <td className="sticky left-0 z-10 p-3 font-medium text-gray-900 border-b border-gray-100 bg-inherit">
                        {state}
                      </td>
                      {matrix.carriers.map(carrier => {
                        const cell = getCellValue(state, carrier.id)
                        const cellKey = getCellKey(state, carrier.id)
                        const isEditing = editingCell === cellKey
                        const isPending = pendingChanges.has(cellKey)

                        return (
                          <td
                            key={carrier.id}
                            className={`p-2 text-center border-b border-gray-100 relative ${
                              isPending ? "ring-2 ring-cyan-400 ring-inset" : ""
                            }`}
                          >
                            {isEditing ? (
                              <div className="flex flex-col items-center gap-2 p-2 bg-white rounded-lg shadow-lg border border-gray-200 absolute z-20 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[180px]">
                                <div className="flex items-center gap-2 w-full">
                                  <span className="text-gray-500">$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={tempCommissionRate}
                                    onChange={(e) => setTempCommissionRate(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleSaveCommission(state, carrier.id)
                                      } else if (e.key === "Escape") {
                                        setEditingCell(null)
                                      }
                                    }}
                                  />
                                </div>
                                <div className="flex items-center gap-1 w-full">
                                  <button
                                    onClick={() => handleSaveCommission(state, carrier.id)}
                                    className="flex-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors flex items-center justify-center gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Set Rate
                                  </button>
                                  <button
                                    onClick={() => handleSetUnavailable(state, carrier.id)}
                                    className="flex-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                  >
                                    Unavail
                                  </button>
                                </div>
                                <button
                                  onClick={() => setEditingCell(null)}
                                  className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : null}

                            <button
                              onClick={() => handleCellClick(state, carrier.id)}
                              className={`w-full py-2 px-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                                cell?.isAvailable
                                  ? "bg-green-100 text-green-700 hover:bg-green-200"
                                  : "bg-red-100 text-red-600 hover:bg-red-200"
                              }`}
                            >
                              {cell?.isAvailable ? (
                                <span className="flex items-center justify-center gap-1">
                                  <DollarSign className="w-3 h-3" />
                                  {cell.commissionRate?.toFixed(2) || "0.00"}
                                </span>
                              ) : (
                                <span className="uppercase text-xs tracking-wide">Unavailable</span>
                              )}
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {successMessage && (
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="w-5 h-5" />
                  <span className="text-sm font-medium">{successMessage}</span>
                </div>
              )}
              {pendingChanges.size > 0 && !successMessage && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-cyan-600">{pendingChanges.size}</span> unsaved change{pendingChanges.size !== 1 ? "s" : ""}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAll}
                disabled={saving || pendingChanges.size === 0}
                className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
